/**
 * SettingsPage.tsx
 * Pagina impostazioni utente con preferenze di tema e altre configurazioni
 */
import React, { useState, useEffect } from 'react';
import {
  Container,
  SimpleGrid,
  Text,
  Group,
  Stack,
  Switch,
  Select,
  Paper,
  Box,
  Divider,
  ThemeIcon,
  Button,
  SegmentedControl,
  Notification,
  Slider,
} from '@mantine/core';
import {
  IconSun,
  IconMoon,
  IconBell,
  IconBellOff,
  IconLayoutKanban,
  IconTable,
  IconDeviceFloppy,
  IconRefresh,
  IconPalette,
  IconSettings,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { GinoriCard, GinoriTitle, GinoriDivider } from '../../components/ui';
import { useTheme } from '../../providers/theme-provider';
import { useOktaAuth } from '../../providers/okta-auth-provider';

/**
 * Chiavi per localStorage
 */
const STORAGE_KEYS = {
  NOTIFICATIONS_ENABLED: 'us_notifications_enabled',
  DEFAULT_VIEW: 'us_default_view',
  KANBAN_COLUMNS: 'us_kanban_columns',
  TABLE_PAGE_SIZE: 'us_table_page_size',
  AUTO_REFRESH: 'us_auto_refresh',
  COMPACT_MODE: 'us_compact_mode',
};

/**
 * Valori di default per le preferenze
 */
const DEFAULT_PREFERENCES = {
  notificationsEnabled: true,
  defaultView: 'kanban',
  kanbanColumns: ['ToDo', 'Doing', 'Testing', 'Done'],
  tablePageSize: 10,
  autoRefresh: true,
  compactMode: false,
};

/**
 * Hook per gestire le preferenze utente salvate in localStorage
 */
function useUserPreferences() {
  const [preferences, setPreferences] = useState(() => {
    // Carica preferenze da localStorage
    const loadedPreferences = { ...DEFAULT_PREFERENCES };

    try {
      const notificationsEnabled = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS_ENABLED);
      if (notificationsEnabled !== null) {
        loadedPreferences.notificationsEnabled = notificationsEnabled === 'true';
      }

      const defaultView = localStorage.getItem(STORAGE_KEYS.DEFAULT_VIEW);
      if (defaultView) {
        loadedPreferences.defaultView = defaultView;
      }

      const tablePageSize = localStorage.getItem(STORAGE_KEYS.TABLE_PAGE_SIZE);
      if (tablePageSize) {
        loadedPreferences.tablePageSize = parseInt(tablePageSize, 10);
      }

      const autoRefresh = localStorage.getItem(STORAGE_KEYS.AUTO_REFRESH);
      if (autoRefresh !== null) {
        loadedPreferences.autoRefresh = autoRefresh === 'true';
      }

      const compactMode = localStorage.getItem(STORAGE_KEYS.COMPACT_MODE);
      if (compactMode !== null) {
        loadedPreferences.compactMode = compactMode === 'true';
      }
    } catch (error) {
      console.error('Errore nel caricamento delle preferenze:', error);
    }

    return loadedPreferences;
  });

  const updatePreference = <K extends keyof typeof DEFAULT_PREFERENCES>(
    key: K,
    value: typeof DEFAULT_PREFERENCES[K]
  ) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));

    // Salva in localStorage
    const storageKey = STORAGE_KEYS[key.toUpperCase().replace(/([A-Z])/g, '_$1').slice(1) as keyof typeof STORAGE_KEYS];
    if (storageKey) {
      localStorage.setItem(storageKey, String(value));
    }
  };

  const resetPreferences = () => {
    setPreferences(DEFAULT_PREFERENCES);
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  };

  return { preferences, updatePreference, resetPreferences };
}

/**
 * Componente sezione impostazioni
 */
interface SettingsSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SettingsSection({ title, icon, children }: SettingsSectionProps) {
  return (
    <GinoriCard elevated>
      <Group gap="sm" mb="lg">
        <ThemeIcon variant="light" size="md" radius="sm" color="ginoriGold">
          {icon}
        </ThemeIcon>
        <Text
          size="md"
          fw={500}
          style={{
            fontFamily: '"Playfair Display", serif',
            letterSpacing: '0.3px',
          }}
        >
          {title}
        </Text>
      </Group>
      {children}
    </GinoriCard>
  );
}

/**
 * Componente riga impostazione con switch
 */
interface SettingRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function SettingRow({ label, description, checked, onChange }: SettingRowProps) {
  return (
    <Group justify="space-between" py="sm">
      <Stack gap={2} style={{ flex: 1 }}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </Stack>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        size="md"
      />
    </Group>
  );
}

export function SettingsPage() {
  const { colorScheme, toggleColorScheme } = useTheme();
  const { userInfo, isAdmin } = useOktaAuth();
  const { preferences, updatePreference, resetPreferences } = useUserPreferences();
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [showResetNotification, setShowResetNotification] = useState(false);

  const handleSaveAll = () => {
    // Le preferenze sono gia salvate automaticamente, mostriamo solo una notifica
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 3000);
  };

  const handleReset = () => {
    resetPreferences();
    setShowResetNotification(true);
    setTimeout(() => setShowResetNotification(false), 3000);
  };

  return (
    <Container size="xl" py="xl">
      <GinoriTitle elegant subtitle="Personalizza la tua esperienza nell'applicazione">
        Impostazioni
      </GinoriTitle>

      {/* Notifiche */}
      {showSaveNotification && (
        <Notification
          icon={<IconCheck size={18} />}
          color="green"
          title="Preferenze salvate"
          mb="md"
          onClose={() => setShowSaveNotification(false)}
        >
          Le tue preferenze sono state salvate con successo.
        </Notification>
      )}

      {showResetNotification && (
        <Notification
          icon={<IconRefresh size={18} />}
          color="blue"
          title="Preferenze reimpostate"
          mb="md"
          onClose={() => setShowResetNotification(false)}
        >
          Le preferenze sono state reimpostate ai valori predefiniti.
        </Notification>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* Aspetto */}
        <SettingsSection title="Aspetto" icon={<IconPalette size={18} />}>
          <Stack gap="md">
            <Group justify="space-between" py="sm">
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Tema
                </Text>
                <Text size="xs" c="dimmed">
                  Scegli tra tema chiaro e scuro
                </Text>
              </Stack>
              <SegmentedControl
                value={colorScheme}
                onChange={() => toggleColorScheme()}
                data={[
                  {
                    value: 'light',
                    label: (
                      <Group gap={4}>
                        <IconSun size={16} />
                        <span>Chiaro</span>
                      </Group>
                    ),
                  },
                  {
                    value: 'dark',
                    label: (
                      <Group gap={4}>
                        <IconMoon size={16} />
                        <span>Scuro</span>
                      </Group>
                    ),
                  },
                ]}
              />
            </Group>

            <Divider />

            <SettingRow
              label="Modalita compatta"
              description="Riduce gli spazi per visualizzare piu contenuto"
              checked={preferences.compactMode}
              onChange={(checked) => updatePreference('compactMode', checked)}
            />
          </Stack>
        </SettingsSection>

        {/* Notifiche */}
        <SettingsSection title="Notifiche" icon={<IconBell size={18} />}>
          <Stack gap="md">
            <SettingRow
              label="Notifiche attive"
              description="Ricevi notifiche per aggiornamenti e scadenze"
              checked={preferences.notificationsEnabled}
              onChange={(checked) => updatePreference('notificationsEnabled', checked)}
            />

            <Divider />

            <Group justify="space-between" py="sm">
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Stato notifiche browser
                </Text>
                <Text size="xs" c="dimmed">
                  Permesso per notifiche push del browser
                </Text>
              </Stack>
              <NotificationPermissionBadge />
            </Group>
          </Stack>
        </SettingsSection>

        {/* Visualizzazione */}
        <SettingsSection title="Visualizzazione Predefinita" icon={<IconLayoutKanban size={18} />}>
          <Stack gap="md">
            <Group justify="space-between" py="sm">
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Vista predefinita task
                </Text>
                <Text size="xs" c="dimmed">
                  Scegli come visualizzare i task per default
                </Text>
              </Stack>
              <SegmentedControl
                value={preferences.defaultView}
                onChange={(value) => updatePreference('defaultView', value as 'kanban' | 'table')}
                data={[
                  {
                    value: 'kanban',
                    label: (
                      <Group gap={4}>
                        <IconLayoutKanban size={16} />
                        <span>Kanban</span>
                      </Group>
                    ),
                  },
                  {
                    value: 'table',
                    label: (
                      <Group gap={4}>
                        <IconTable size={16} />
                        <span>Tabella</span>
                      </Group>
                    ),
                  },
                ]}
              />
            </Group>

            <Divider />

            <Group justify="space-between" py="sm">
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Righe per pagina nelle tabelle
                </Text>
                <Text size="xs" c="dimmed">
                  Numero di elementi visualizzati per pagina
                </Text>
              </Stack>
              <Select
                value={String(preferences.tablePageSize)}
                onChange={(value) => updatePreference('tablePageSize', parseInt(value || '10', 10))}
                data={[
                  { value: '5', label: '5 righe' },
                  { value: '10', label: '10 righe' },
                  { value: '25', label: '25 righe' },
                  { value: '50', label: '50 righe' },
                ]}
                w={120}
              />
            </Group>
          </Stack>
        </SettingsSection>

        {/* Comportamento */}
        <SettingsSection title="Comportamento" icon={<IconSettings size={18} />}>
          <Stack gap="md">
            <SettingRow
              label="Aggiornamento automatico"
              description="Aggiorna i dati automaticamente ogni 30 secondi"
              checked={preferences.autoRefresh}
              onChange={(checked) => updatePreference('autoRefresh', checked)}
            />

            <Divider />

            <Box py="sm">
              <Text size="sm" fw={500} mb="xs">
                Informazioni sessione
              </Text>
              <Stack gap="xs">
                <Group gap="xs">
                  <Text size="xs" c="dimmed">Utente:</Text>
                  <Text size="xs">{userInfo?.email || '-'}</Text>
                </Group>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">Sessione iniziata:</Text>
                  <Text size="xs">{new Date().toLocaleDateString('it-IT')}</Text>
                </Group>
              </Stack>
            </Box>
          </Stack>
        </SettingsSection>
      </SimpleGrid>

      <GinoriDivider elegant />

      {/* Azioni */}
      <Group justify="flex-end" mt="xl">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconRefresh size={16} />}
          onClick={handleReset}
        >
          Reimposta Predefiniti
        </Button>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={handleSaveAll}
        >
          Salva Preferenze
        </Button>
      </Group>

      {/* Info per admin */}
      {isAdmin && (
        <>
          <GinoriDivider elegant />
          <GinoriCard elevated mt="lg">
            <Group gap="sm" mb="sm">
              <ThemeIcon variant="light" size="md" radius="sm" color="red">
                <IconSettings size={18} />
              </ThemeIcon>
              <Text
                size="md"
                fw={500}
                style={{
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Area Amministratore
              </Text>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              Come amministratore hai accesso a configurazioni aggiuntive del sistema.
            </Text>
            <Button
              variant="light"
              color="red"
              component="a"
              href="/admin/cache-dashboard"
            >
              Vai alla Dashboard Cache
            </Button>
          </GinoriCard>
        </>
      )}
    </Container>
  );
}

/**
 * Componente per mostrare lo stato dei permessi notifiche browser
 */
function NotificationPermissionBadge() {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(window.Notification.permission);
    } else {
      setPermission('unsupported');
    }
  }, []);

  const requestPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const result = await window.Notification.requestPermission();
      setPermission(result);
    }
  };

  if (permission === 'unsupported') {
    return (
      <Text size="xs" c="dimmed">
        Non supportato
      </Text>
    );
  }

  if (permission === 'granted') {
    return (
      <Group gap={4}>
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-green-6)',
          }}
        />
        <Text size="xs" c="green">
          Consentite
        </Text>
      </Group>
    );
  }

  if (permission === 'denied') {
    return (
      <Group gap={4}>
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-red-6)',
          }}
        />
        <Text size="xs" c="red">
          Bloccate
        </Text>
      </Group>
    );
  }

  return (
    <Button size="xs" variant="light" onClick={requestPermission}>
      Richiedi permesso
    </Button>
  );
}

export default SettingsPage;
